import dayjs, { extend } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { parsePhoneNumberFromString, type MetadataJson } from 'libphonenumber-js/core';

import { ISelectMap } from '@models';
import { EVA_CSV_DATE_FORMAT, EVA_CSV_DATE_FORMAT_ALTERNATE, LegacyPriorProductionTypeMap } from '@configs/constants';
import phoneMetadataCustom from '@utils/phone-metadata.custom.json';

// Extend dayjs with plugins
extend(customParseFormat);

export const reduceMap = (items: ISelectMap[]) =>
  items?.reduce(
    (acc, item) => {
      acc[item.code] = item.decode;
      return acc;
    },
    {} as Record<string, string>,
  );

// A plain integer or decimal such as 1500 or 1500.50, without separators or currency symbols
export const isNumeric = (value: string) => {
  return /^-?\d+(\.\d+)?$/.test(value);
};

// Plugin already extended at the top of the file

/**
 * Checks if a string is in one of the EVA CSV date formats
 * @param value The string to check
 * @returns True if the string is in a valid EVA CSV date format
 */
export const isEVADateFormat = (value: string): boolean => {
  return dayjs(value, EVA_CSV_DATE_FORMAT, true).isValid() || dayjs(value, EVA_CSV_DATE_FORMAT_ALTERNATE, true).isValid();
};

/**
 * Gets the current year, with special handling for early January dates.
 * If the date is between January 1st and the first Friday of the year,
 * it returns the previous year, otherwise returns the current year.
 *
 * @returns {number} The current year, adjusted for business logic
 */
export const getCurrentYear = (): number => {
  const now = dayjs();
  const year = now.year();
  const month = now.month() + 1; // dayjs months are 0-indexed
  const day = now.date();

  // Only apply special logic for early January
  if (month === 1 && day < 8) {
    // Get the first day of the year
    const jan1 = dayjs(`${year}-01-01`);
    const jan1DayOfWeek = jan1.day(); // 0 = Sunday, 6 = Saturday

    // Calculate days until first Friday (5 = Friday)
    // If Jan 1 is Friday (day 5), then it's 0 days until Friday
    // If Jan 1 is Saturday (day 6), then it's 6 days until Friday
    // If Jan 1 is Sunday (day 0), then it's 5 days until Friday
    const daysUntilFirstFriday = (5 - jan1DayOfWeek + 7) % 7;

    // Calculate the date of the first Friday
    const firstFridayDate = 1 + daysUntilFirstFriday;

    // If current day is before the first Friday, consider it part of previous year
    if (day < firstFridayDate) {
      return year - 1;
    }
  }

  return year;
};

/**
 * Ensures a phone number is properly formatted in E.164 format,
 * which is commonly used for storage and API communication.
 * This function validates and reformats the input as necessary.
 *
 * The E.164 format includes the country code and excludes any additional formatting symbols.
 * For example:
 * - US number "6461234567" (without country code) will be formatted as "+16461234567"
 * - UK number "+44 1234 567890" (with formatting symbols) will be formatted as "+441234567890"
 *
 * @param {string} phone - The phone number to validate and format.
 * @returns {string | undefined} - The formatted phone number in E.164 format, or undefined if the input is invalid.
 */
export const formatPhoneNumber = (phone: string | undefined): string | undefined => {
  // Return undefined if the phone number is not provided.
  if (!phone) return undefined;

  // Parse the phone number, assuming "US" as a fallback country if the country code is missing.
  const parsedPhone = parsePhoneNumberFromString(phone, 'US', phoneMetadataCustom as MetadataJson);

  // Return the formatted E.164 phone number if valid; otherwise, return undefined.
  return parsedPhone ? parsedPhone.format('E.164') : undefined;
};

/**
 * Formats a phone number for display in the format "+countryCode nationalNumber".
 * For example:
 * - US number "+16461234567" will be formatted as "+1 (646) 123-4567"
 * - UK number "+441234567890" will be formatted as "+44 1234 567890"
 *
 * @param {string} phone - The phone number to format.
 * @returns {string} - The formatted phone number, or the original input if parsing fails.
 */
export const formatPhoneNumberInternational = (phone: string): string => {
  // Return the original input if phone is not provided.
  if (!phone) return phone;

  // Parse the phone number with a default country context ("US") in case the number lacks a country code.
  const parsedPhone = parsePhoneNumberFromString(phone, 'US', phoneMetadataCustom as MetadataJson);

  // If parsing fails, return the original phone number.
  if (!parsedPhone) return phone;

  // Format the number in national format (e.g., "(646) 123-4567" for US numbers).
  const nationalNumber = parsedPhone.formatNational();

  // Combine the country calling code with the national number, prefixed by "+".
  return `+${parsedPhone.countryCallingCode} ${nationalNumber}`;
};

interface Bio {
  language: string;
  value: string;
}

export function didTextAreaChange(selectedLang: string, langTextArea: string, bio: Bio[]) {
  if (selectedLang && langTextArea) {
    const bioObjArr = bio.filter((bioObj) => bioObj.language === selectedLang);
    if (bioObjArr?.length > 0) {
      return langTextArea !== bioObjArr[0].value;
    } else if (langTextArea?.length > 0) return true;
  }
  return false;
}

export const getUploaderUrl = (fileName: string) => `${IMAGE_UPLOADER_URL}${fileName}`;

/**
 * Normalizes a prior production transaction type to the code the API stores.
 * Records saved before the data cleanup still carry the older spelling, so both resolve to the same code.
 * Surrounding whitespace is dropped to match the API, which reads the column through lower(btrim(type)).
 * @param type The transaction type as entered or as returned by the API
 * @returns The canonical, trimmed, lower cased transaction type
 */
export const toCanonicalPriorProductionType = (type?: string): string => {
  const lowerCasedType = type?.trim().toLowerCase() || '';
  return LegacyPriorProductionTypeMap[lowerCasedType] || lowerCasedType;
};

/**
 * Formats an instant as its calendar date in the America/New_York timezone.
 * Team membership dates are Eastern days, so the browser's local date cannot be used.
 * @param date The instant to convert, defaulting to now
 * @returns The Eastern calendar date in YYYY-MM-DD format
 */
export const getEasternDay = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const partValue = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${partValue('year')}-${partValue('month')}-${partValue('day')}`;
};
